import { Controller } from "@hotwired/stimulus"

// Keeps the guided-design chat scrolled to the newest message AND paces the bot:
// the server's replies come back instantly, so brand-new agent bubbles are briefly
// hidden behind a "typing" indicator to make the robot feel like it's thinking.
//
// The whole thread re-renders on every step, so we track how many bubbles have
// already been revealed (per chat, in sessionStorage). Only bubbles past that
// count are "new"; the visitor's own bubbles stay instant, and a reload never
// replays the effect. The one exception is a brand-new chat's single greeting,
// which is paced the first time it appears in the tab.
export default class extends Controller {
  static targets = ["anchor", "bubble", "tail", "typing"]
  static values = {
    key: String,
    delay: { type: Number, default: 2500 }
  }

  connect() {
    this.timer = null
    this.queuedAgentBubbles = []
    this.paceNewBubbles()
    this.scrollToEnd()
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  paceNewBubbles() {
    const total = this.bubbleTargets.length
    const stored = this.storedRevealCount()
    const revealed = stored === null ? this.initialRevealCount(total) : stored

    if (total <= revealed || !this.hasTypingTarget) return this.remember(total)

    // Gate only the just-arrived agent bubbles. If the new tail is just the
    // visitor's own message (no agent reply), there's nothing to pace.
    const fresh = this.bubbleTargets.slice(revealed)
    this.queuedAgentBubbles = fresh
      .map((bubble, offset) => ({ bubble, index: revealed + offset }))
      .filter(({ bubble }) => bubble.dataset.role === "agent")

    if (this.queuedAgentBubbles.length === 0) return this.remember(total)

    // Inline style (not [hidden]) so it beats DaisyUI's `.chat { display: grid }`.
    this.queuedAgentBubbles.forEach(({ bubble }) => { bubble.style.display = "none" })
    if (this.hasTailTarget) this.tailTarget.style.display = "none"

    this.revealNextAgentBubble()
  }

  revealNextAgentBubble() {
    const next = this.queuedAgentBubbles.shift()

    if (!next) {
      if (this.hasTailTarget) this.tailTarget.style.display = ""
      this.remember(this.bubbleTargets.length)
      this.scrollToEnd()
      return
    }

    const indicator = this.showTyping(next.bubble)
    this.scrollToEnd()

    this.timer = setTimeout(() => {
      indicator.remove()
      next.bubble.style.display = ""
      this.remember(next.index + 1)
      this.scrollToEnd()
      this.revealNextAgentBubble()
    }, this.delayValue)
  }

  showTyping(beforeBubble) {
    const node = this.typingTarget.content.firstElementChild.cloneNode(true)
    beforeBubble.before(node)
    return node
  }

  storedRevealCount() {
    if (!this.keyValue) return null

    const stored = sessionStorage.getItem(this.keyValue)
    if (stored === null) return null

    const count = parseInt(stored, 10)
    return Number.isNaN(count) ? null : count
  }

  initialRevealCount(total) {
    return this.isInitialGreetingOnly(total) ? 0 : total
  }

  isInitialGreetingOnly(total = this.bubbleTargets.length) {
    return total === 1 && this.bubbleTargets[0]?.dataset.role === "agent"
  }

  remember(count) {
    if (this.keyValue) sessionStorage.setItem(this.keyValue, String(count))
  }

  scrollToEnd() {
    requestAnimationFrame(() => {
      if (document.activeElement?.tagName === "GMP-PLACE-AUTOCOMPLETE") return
      const target = this.hasAnchorTarget ? this.anchorTarget : this.element
      target.scrollIntoView({ behavior: "instant", block: "end" })
    })
  }
}
